import { PeriodRequest } from '@src/api/types/communityDashboard';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface Column {
  title: string;
  key: string;
  width?: string;
  nowrap?: boolean;
  sortable?: boolean;
}

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  preset: PeriodRequest | null;
};

interface DataTableProps {
  title?: string;
  columns: Column[];
  data: Record<string, string | number | null>[];
  dateRange?: DateRange;
  seeMoreLink?: string;
  marginTop?: string;
  padding?: string;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  pagination?: Pagination;
  keyword?: string;
  handlePageChange?: (page: number) => void;
}

const DataTable: React.FC<DataTableProps> = ({
  title,
  columns,
  data,
  dateRange,
  seeMoreLink,
  marginTop = 'mt-10',
  padding = 'p-6',
  onSort,
  sortKey,
  sortDirection,
  pagination,
  keyword,
  handlePageChange,
}) => {
  const navigate = useNavigate();
  const containerClasses = ['rounded-[4px]', 'bg-white', marginTop, padding]
    .filter(Boolean)
    .join(' ');

  const handleSort = (column: Column) => {
    if (!column.sortable || !onSort) {
      return;
    }
    const newDirection = sortKey === column.key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(column.key, newDirection);
  };

  const getSortIcon = (column: Column) => {
    if (!column.sortable) {
      return null;
    }
    return (
      <div className='flex flex-col'>
        {/* ▲ボタン */}
        <div
          className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:transition-opacity after:[clip-path:polygon(50%_0,0_100%,100%_100%)] ${
            sortKey === column.key && sortDirection === 'asc'
              ? 'after:opacity-100'
              : 'after:opacity-20'
          } hover:after:opacity-80`}
        ></div>
        {/* ▼ボタン */}
        <div
          className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:transition-opacity after:[clip-path:polygon(50%_100%,0_0,100%_0)] ${
            sortKey === column.key && sortDirection === 'desc'
              ? 'after:opacity-100'
              : 'after:opacity-20'
          } hover:after:opacity-80`}
        ></div>
      </div>
    );
  };

  const renderCellContent = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return value;
  };

  const setTruncatedTitle = (el: HTMLElement | null, text: string): void => {
    if (!el) {
      return;
    }

    if (el.scrollWidth > el.clientWidth) {
      el.setAttribute('title', text);
    } else {
      el.removeAttribute('title');
    }
  };

  return (
    <div className={containerClasses}>
      {title && <h2 className='text-[24px] font-semibold leading-[1.75]'>{title}</h2>}
      <div className={`${title ? 'mt-6' : ''} overflow-auto`}>
        <table className='size-full min-w-[872px] table-fixed text-sm'>
          <colgroup>
            {columns.map((column, index) => (
              <col
                key={index}
                className={column.width || ''}
              />
            ))}
          </colgroup>
          <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
            <tr className='h-[42px]'>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-xs font-semibold ${column.nowrap ? 'whitespace-nowrap' : ''}`}
                  onClick={() => column.sortable && handleSort(column)}
                >
                  <span className='flex items-center justify-start gap-5'>
                    {column.title}
                    {column.sortable && getSortIcon(column)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.length > 0
              ? data?.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className='[&:nth-child(2n)]:bg-gray-10'
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`truncate px-4 py-[18px] text-sm`}
                        ref={(el) =>
                          setTruncatedTitle(el, String(renderCellContent(row[column.key])))
                        }
                      >
                        {renderCellContent(row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))
              : null}
          </tbody>
        </table>
        {!keyword && pagination?.total === 0 ? (
          <p className='mt-4 text-sm'>データがありません。</p>
        ) : null}
      </div>
      {seeMoreLink && (
        <div className='mt-2 flex justify-end'>
          <button
            className='text-sm text-primary-500 underline hover:no-underline'
            onClick={() =>
              navigate(seeMoreLink, {
                state: {
                  startDate: dateRange?.startDate,
                  endDate: dateRange?.endDate,
                  preset: dateRange?.preset,
                },
              })
            }
          >
            もっと見る
          </button>
        </div>
      )}
      {pagination && pagination.total !== 0 && handlePageChange && (
        <div className='mt-6'>
          <PaginationComponent
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
